import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import times from 'lodash/times';

import P from '@deliveroo/consumer-component-library/components/P';
import Span from '@deliveroo/consumer-component-library/components/Span';
import MobileBanner from '../MobileBanner';
import { maybeGetClientCookie, maybeSetClientCookie } from '../../utils/cookies/client';
import i18n from '../../utils/i18n';
import styles from './styles.scss';

const DAYS_TO_EXPIRE = 20;

const timestamp = new Date();
const expiryTimestamp = timestamp.setDate(timestamp.getDate() + DAYS_TO_EXPIRE);
const cookieOptions = {
  path: '/',
  expires: new Date(expiryTimestamp),
};

/**
 * A banner displayed on mobile web linking to our native app downloads
 * Though they look similar, the AppBanner uses a different implementation across orderweb and CWA
 */
export default class AppBanner extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isDismissed: true,
    };
  }

  componentDidMount() {
    const cookie = maybeGetClientCookie('has_dismissed_mobile_banner');

    // try to set cookie if it doesn't exist
    if (cookie === null) {
      maybeSetClientCookie('has_dismissed_mobile_banner', 'false', cookieOptions);
    }

    // The browser-cookie API requires values to be string; hence the comparison:
    this.setState({
      isDismissed: cookie === 'true',
    });
  }

  dismissAppBanner = () => {
    maybeSetClientCookie('has_dismissed_mobile_banner', 'true', cookieOptions);
    this.setState({ isDismissed: true });
    this.props.trackCloseAppBanner(this.props.page);
  };

  appBannerTitle() {
    const { platform } = this.props;

    switch (platform) {
      case 'android':
        return i18n.gettext('Deliveroo for Android');
      case 'ios':
        return i18n.gettext('Deliveroo for iOS');
      default:
        return 'Deliveroo';
    }
  }

  renderReviewStars() {
    const { rating } = this.props;

    if (typeof rating === 'number') {
      const wholeStars = Math.floor(rating);
      const halfStar = rating - wholeStars > 0;
      const stars = [];

      times(wholeStars, (i) => {
        stars.push(
          <span key={`full-${i}`} className={classnames(styles.star, styles.full)}>
            ★
          </span>,
        );
      });

      if (halfStar) {
        stars.push(
          <span key="half" className={classnames(styles.star, styles.half)}>
            ☆
          </span>,
        );
      }

      times(5 - stars.length, (i) => {
        stars.push(
          <span key={`empty-${i}`} className={classnames(styles.star, styles.empty)}>
            ☆
          </span>,
        );
      });

      return <span className={styles.stars}>{stars}</span>;
    }

    return null;
  }

  renderPlatformIcon = () => (
    <div className={classnames(styles.appIcon, styles[this.props.platform])} />
  );

  renderBody = () => (
    <div className={styles.stats}>
      <P size="small" color="white">
        {this.appBannerTitle()}
      </P>
      {this.props.rating > 0 && (
        <span className={styles.rating}>
          {this.renderReviewStars()}
          <span className={styles.count}>
            <Span size="small">({this.props.reviews})</Span>
          </span>
        </span>
      )}
    </div>
  );

  render() {
    const { platform, trackVisitAppBanner, page } = this.props;
    const { isDismissed } = this.state;

    if (isDismissed) {
      return null;
    }

    return (
      <MobileBanner
        isDismissed={isDismissed}
        buttonText={i18n.gettext('Install')}
        buttonLink={`/app?platform=${platform}`}
        renderWrapper={(children) => <div className={styles.wrapper}>{children}</div>}
        renderImage={this.renderPlatformIcon}
        renderBody={this.renderBody}
        onClose={() => this.dismissAppBanner(page)}
        onButtonClick={() => trackVisitAppBanner(page)}
        page={page}
      />
    );
  }
}

AppBanner.propTypes = {
  /**
   * Platform the app banner is for.
   */
  platform: PropTypes.oneOf(['ios', 'android']).isRequired,

  /**
   * String representation of the number of reviews we’ve received for that platform, e.g. "12,345".
   */
  reviews: PropTypes.string.isRequired,

  /**
   * Numeric representation of the average review score (between 0 and 5.0).
   */
  rating: PropTypes.number.isRequired,

  /**
   * String representing what page the banner is displayed on.
   */
  page: PropTypes.string.isRequired,

  /**
   * Event handler for tracking when the app banner is closed.
   */
  trackCloseAppBanner: PropTypes.func.isRequired,

  /**
   * Event handler for tracking when the user follows the link from the banner.
   */
  trackVisitAppBanner: PropTypes.func.isRequired,
};
